import { navigateTo, useCookie, useNuxtApp } from "#app";
import { storeToRefs } from "pinia";
import { useAllowedMenus } from "~/store/menu/allowedMenus";
import { usePageStore } from "~/store/page/pageStore";
import { useUserStore } from "~/store/user/userStore";

export type SessionLogoutReason =
	| "missing-token"
	| "expired-token"
	| "unauthorized"
	| "manual-logout"
	| "missing-company";

export type SessionAuthErrorCode =
	| "AUTH_MISSING_TOKEN"
	| "AUTH_SESSION_EXPIRED"
	| "AUTH_UNAUTHORIZED"
	| "AUTH_MISSING_COMPANY";

export class SessionAuthError extends Error {
	status: number;
	code: SessionAuthErrorCode;
	reason: SessionLogoutReason;
	isAuthenticationError: true;

	constructor(reason: SessionLogoutReason, message?: string, status = 401) {
		super(message ?? getAuthMessage(reason));
		this.name = "SessionAuthError";
		this.status = status;
		this.reason = reason;
		this.code = getAuthCode(reason);
		this.isAuthenticationError = true;
		Object.setPrototypeOf(this, SessionAuthError.prototype);
	}
}

let logoutPromise: Promise<unknown> | null = null;
let expirationTimeout: ReturnType<typeof setTimeout> | null = null;
let sessionPollingInterval: ReturnType<typeof setInterval> | null = null;

let lastActivityTimestamp = Date.now();
let hasRecentActivity = false;
let throttleTimer: ReturnType<typeof setTimeout> | null = null;

const SESSION_IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 horas

function updateActivity() {
	lastActivityTimestamp = Date.now();
	hasRecentActivity = true;
}

function handleUserActivity() {
	if (throttleTimer) return;
	updateActivity();
	throttleTimer = setTimeout(() => {
		throttleTimer = null;
	}, 1000);
}

function getAuthCode(reason: SessionLogoutReason): SessionAuthErrorCode {
	switch (reason) {
		case "missing-token":
			return "AUTH_MISSING_TOKEN";
		case "expired-token":
			return "AUTH_SESSION_EXPIRED";
		case "missing-company":
			return "AUTH_MISSING_COMPANY";
		default:
			return "AUTH_UNAUTHORIZED";
	}
}

function getAuthMessage(reason: SessionLogoutReason): string {
	switch (reason) {
		case "missing-token":
			return "Usuário não autenticado.";
		case "expired-token":
			return "Sessão expirada. Faça login novamente.";
		case "manual-logout":
			return "Logout realizado.";
		case "missing-company":
			return "Empresa não definida na sessão. Faça login novamente.";
		default:
			return "Usuário não autorizado.";
	}
}

/** Mensagens que indicam empresa ausente — qualquer tela deve ir ao login. */
const MENSAGENS_EMPRESA_AUSENTE = [
	"Empresa não definida na sessão",
	"Empresa não definida na sessão.",
	"Empresa não definida.",
];

/**
 * Indica se o erro/texto trata de empresa ausente na sessão.
 */
export function isErroEmpresaAusente(erro: unknown): boolean {
	if (!erro) return false;
	const msg =
		typeof erro === "string"
			? erro
			: erro instanceof Error
				? erro.message
				: typeof erro === "object" && erro !== null && "message" in erro
					? String((erro as { message: unknown }).message)
					: "";
	const normalizada = msg.trim().toLowerCase();
	if (!normalizada) return false;
	return MENSAGENS_EMPRESA_AUSENTE.some(m =>
		normalizada.includes(m.toLowerCase().replace(/\.$/, "")),
	);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
	try {
		const payload = token.split(".")[1];
		if (!payload) {
			return null;
		}

		const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), "=");
		return JSON.parse(atob(padded)) as Record<string, unknown>;
	} catch {
		return null;
	}
}

function getTokenExpiration(token: string): number | null {
	const payload = decodeJwtPayload(token);
	const exp = payload?.exp;
	return typeof exp === "number" ? exp * 1000 : null;
}

function clearExpirationTimeout() {
	if (expirationTimeout) {
		clearTimeout(expirationTimeout);
		expirationTimeout = null;
	}
}

function clearPersistedSessionState() {
	const userStore = useUserStore();
	const pageStore = usePageStore();
	const allowedMenus = useAllowedMenus();

	userStore.setUserID(0);
	userStore.setCompaniesNames([]);
	userStore.setCompaniesID([]);
	userStore.setCompanyID(undefined as never);
	userStore.setCompanyName("");
	userStore.setUserType("");
	userStore.setAuthStatus(false);
	userStore.setAuthenticationLogout(false);
	userStore.setUserRoutines([]);
	userStore.setCurrentPageName("");
	userStore.setMenuSelected("" as never);

	pageStore.setShowSearchBar(false);
	pageStore.setTituloPagina("");
	pageStore.setLayout("main");
	pageStore.setAuthStatus(false);
	pageStore.setCurrentPage("home");
	pageStore.setPreviousPage("home");
	pageStore.setCurrentOptionName("");
	pageStore.setCompanyName("");
	pageStore.setMaximized(true);

	allowedMenus.setMenus([]);

	localStorage.removeItem("userStore");
	localStorage.removeItem("allowedMenus");
	sessionStorage.removeItem("pageStore");
}

/**
 * Leva para a tela de login.
 *
 * Devolve o que `navigateTo` devolver, e isso é essencial: **dentro de um
 * middleware de rota** `navigateTo` não navega — ela apenas devolve o destino,
 * que o middleware precisa retornar para o Nuxt executar o redirecionamento
 * (ver `navigateTo` em `nuxt/dist/app/composables/router.js`). Engolir esse
 * retorno faz a navegação original seguir em frente como se nada tivesse sido
 * pedido. Fora de middleware o retorno é a promessa do `router.replace`.
 */
async function redirectToLogin(): Promise<unknown> {
	if (window.location.pathname === "/login") {
		return undefined;
	}

	return navigateTo("/login", { replace: true });
}

export function isAuthenticationError(error: unknown): error is SessionAuthError {
	if (error instanceof SessionAuthError) {
		return true;
	}

	if (!error || typeof error !== "object") {
		return false;
	}

	const candidate = error as {
		isAuthenticationError?: boolean;
		code?: string;
		status?: number;
	};

	return candidate.isAuthenticationError === true
		|| candidate.code === "AUTH_MISSING_TOKEN"
		|| candidate.code === "AUTH_SESSION_EXPIRED"
		|| candidate.code === "AUTH_UNAUTHORIZED";
}

export function createSessionAuthError(reason: SessionLogoutReason, message?: string, status?: number) {
	return new SessionAuthError(reason, message, status);
}

export function useSessionManager() {
	const userStore = useUserStore();
	const pageStore = usePageStore();
	const { authenticated } = storeToRefs(userStore);
	const accessTokenCookie = useCookie<string | null>("accessToken");
	const userCookie = useCookie<string | null>("user");

	/**
	 * Encerra a sessão e leva ao login.
	 *
	 * O retorno propaga o de {@link redirectToLogin}: quem chama de dentro de um
	 * middleware **precisa retorná-lo**, senão o redirecionamento é descartado.
	 */
	async function logout(reason: SessionLogoutReason): Promise<unknown> {
		if (logoutPromise) {
			return logoutPromise;
		}

		logoutPromise = (async () => {
			clearExpirationTimeout();
			accessTokenCookie.value = null;
			userCookie.value = null;
			clearPersistedSessionState();
			userStore.setAuthStatus(false);
			pageStore.setAuthStatus(false);
			pageStore.setCurrentPage("Login");
			userStore.setAuthenticationLogout(
				reason === "expired-token"
				|| reason === "unauthorized"
				|| reason === "missing-token"
				|| reason === "missing-company",
			);
			return redirectToLogin();
		})().finally(() => {
			logoutPromise = null;
		});

		return logoutPromise;
	}

	async function handleAuthFailure(reason: SessionLogoutReason, message?: string, status = 401): Promise<never> {
		const error = new SessionAuthError(reason, message, status);
		await logout(reason);
		throw error;
	}

	/**
	 * Empresa da sessão (companyID). Se não houver, encerra a sessão e vai ao login.
	 * Use em qualquer tela/API que dependa de EMPRESA.
	 *
	 * @returns ID numérico da empresa
	 * @throws SessionAuthError se a empresa não estiver definida (após redirecionar)
	 */
	function exigirEmpresaNaSessao(): number {
		const userStore = useUserStore();
		const id = Number(userStore.companyID ?? 0);
		if (!id || Number.isNaN(id)) {
			void logout("missing-company");
			throw new SessionAuthError(
				"missing-company",
				"Empresa não definida na sessão. Faça login novamente.",
			);
		}
		return id;
	}

	/**
	 * Se o erro indicar empresa ausente, faz logout → login.
	 * @returns true se redirecionou
	 */
	function tratarErroEmpresaAusente(erro: unknown): boolean {
		if (!isErroEmpresaAusente(erro)) return false;
		void logout("missing-company");
		return true;
	}

	function scheduleExpirationFromToken(token?: string | null) {
		clearExpirationTimeout();
		const tokenValue = token ?? accessTokenCookie.value;
		if (!tokenValue) {
			return;
		}

		const expirationAt = getTokenExpiration(tokenValue);
		if (!expirationAt) {
			return;
		}

		const timeout = expirationAt - Date.now();
		if (timeout <= 0) {
			void logout("expired-token");
			return;
		}

		expirationTimeout = setTimeout(() => {
			void logout("expired-token");
		}, timeout);
	}

	function syncSessionFromToken(token?: string | null) {
		const tokenValue = token ?? accessTokenCookie.value;
		authenticated.value = Boolean(tokenValue);
		pageStore.setAuthStatus(Boolean(tokenValue));
		userStore.setAuthStatus(Boolean(tokenValue));
		scheduleExpirationFromToken(tokenValue);
	}

	function getAccessTokenExpiration(token?: string | null) {
		const tokenValue = token ?? accessTokenCookie.value;
		return tokenValue ? getTokenExpiration(tokenValue) : null;
	}

	let failedAuthorizationAttempts = 0;

	function initSessionPolling() {
		if (import.meta.server) return; // Rodar apenas no cliente
		if (sessionPollingInterval) return; // Já inicializado

		updateActivity();

		const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
		events.forEach((event) => {
			window.addEventListener(event, handleUserActivity, { passive: true });
		});

		sessionPollingInterval = setInterval(async () => {
			if (!authenticated.value) return;

			// Verificar timeout de ociosidade no frontend
			if (Date.now() - lastActivityTimestamp > SESSION_IDLE_TIMEOUT_MS) {
				void logout("expired-token");
				return;
			}

			try {
				const { $r3fetchdata } = useNuxtApp();
				
				const response = await $r3fetchdata.post<any, any>("/api/auth/verify", {
					active: hasRecentActivity,
				});

				const newAccessToken = response?.accessToken || response?.data?.accessToken;

				hasRecentActivity = false;
				failedAuthorizationAttempts = 0;

				if (newAccessToken) {
					accessTokenCookie.value = newAccessToken;
					syncSessionFromToken(newAccessToken);
				}
			} catch (error) {
				failedAuthorizationAttempts++;
				if (failedAuthorizationAttempts >= 3) {
					void logout("unauthorized");
				}
			}
		}, 10000);
	}

	return {
		logout,
		handleAuthFailure,
		exigirEmpresaNaSessao,
		tratarErroEmpresaAusente,
		scheduleExpirationFromToken,
		syncSessionFromToken,
		getAccessTokenExpiration,
		initSessionPolling,
	};
}
